import { CurrencyFormat, InvestorHelper } from "@securitize/reactjs-sid-utils";
import { SID } from "@securitize/domain";
import {
  KybSectionStatus,
  KybSectionStatuses,
} from "../data/dtos/onboarding-status.dto";
import { ActionButton } from "../components/shared/onboarding/EntityOnboardingCard";
import { translationKeys } from "../contexts/I18nContext";
import { TranslationKey } from "../contexts/I18nContext/translationKeys";
import { KycStatus } from "../types/KycStatus";

export function shouldShowEntityOnboarding(
  verificationStatus: KycStatus,
  investorType: string,
): boolean {
  return (
    investorType === SID.InvestorTypes.ENTITY &&
    (verificationStatus === KycStatus.UPDATES_REQUIRED ||
      verificationStatus === KycStatus.NOT_VERIFIED ||
      verificationStatus === KycStatus.PENDING)
  );
}

const regularCurrencies: readonly string[] = ["USD", "EUR"];

export interface SectionUIStatus {
  chipColor:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  chipLabel: TranslationKey;
  actionButton: ActionButton;
}

const isRegularCurrency = (currency: string): boolean =>
  regularCurrencies.some(
    (regularCurrency) =>
      regularCurrency.toLowerCase() === currency.toLowerCase(),
  );

interface CurrencyFormatterOptions {
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export const currencyFormatter = ({
  currency = "USD",
  minimumFractionDigits,
  maximumFractionDigits,
}: CurrencyFormatterOptions) => {
  const locale: string = InvestorHelper.getLocale();

  const instance = new CurrencyFormat({
    overrideLocale: locale,
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
    ...(isRegularCurrency(currency) ? {} : { isCrypto: true }),
  });

  return instance;
};

export function getSectionData(status?: KybSectionStatus): SectionUIStatus {
  switch (status) {
    case KybSectionStatuses.COMPLETED:
      return {
        chipColor: "success",
        chipLabel: translationKeys.ONBOARDING_CARD_STATUS_LABEL_COMPLETED,
        actionButton: ActionButton.REVIEW,
      };
    case KybSectionStatuses.IN_PROGRESS:
      return {
        chipColor: "warning",
        chipLabel: translationKeys.ONBOARDING_CARD_STATUS_LABEL_IN_PROGRESS,
        actionButton: ActionButton.MANAGE,
      };
    case KybSectionStatuses.UPDATES_REQUIRED:
      return {
        chipColor: "warning",
        chipLabel:
          translationKeys.ONBOARDING_CARD_STATUS_LABEL_UPDATES_REQUIRED,
        actionButton: ActionButton.MANAGE,
      };
    default:
      return {
        chipColor: "error",
        chipLabel: translationKeys.ONBOARDING_CARD_STATUS_LABEL_NOT_COMPLETED,
        actionButton: ActionButton.MANAGE,
      };
  }
}
